import { useEffect, useState } from 'react'
import {
  deleteDocument,
  listDocumentChunks,
  listDocuments,
  listShowcases,
  reindexAllDocuments,
  reindexDocument,
  uploadDocumentFile,
  uploadDocumentUrl,
  type AdminDocument,
} from '../lib/api'
import type { Showcase } from '@k4ia/shared'
import { FileButton } from '../components/ui'
import { WebseitenStand } from '../components/WebseitenStand'

const STATUS_LABEL: Record<AdminDocument['status'], string> = {
  uploaded: '⏳ hochgeladen',
  indexing: '⚙️ wird indexiert',
  indexed: '✅ indexiert',
  error: '❌ Fehler',
}

export function DocumentsPage() {
  const [documents, setDocuments] = useState<AdminDocument[]>([])
  const [showcases, setShowcases] = useState<Showcase[]>([])
  const [url, setUrl] = useState('')
  const [title, setTitle] = useState('')
  const [showcaseId, setShowcaseId] = useState('')
  const [openChunksFor, setOpenChunksFor] = useState<string | null>(null)
  const [chunks, setChunks] = useState<{ id: string; text: string; position: number }[]>([])

  async function refresh() {
    const [{ documents }, { showcases }] = await Promise.all([listDocuments(), listShowcases()])
    // Wissen ohne Showcase hat seine eigene Seite („Wissen"); hier steht, was
    // zu einem Stand gehört.
    setDocuments(documents.filter((d) => d.showcaseId !== null))
    setShowcases(showcases)
  }

  useEffect(() => {
    void refresh()
    const interval = setInterval(() => void refresh(), 4000) // poll while ingest runs async
    return () => clearInterval(interval)
  }, [])

  async function handleFileUpload(file: File) {
    if (!showcaseId) return
    await uploadDocumentFile(file, title || undefined, showcaseId)
    setTitle('')
    await refresh()
  }

  async function handleUrlUpload() {
    if (!url || !showcaseId) return
    await uploadDocumentUrl(url, title || undefined, showcaseId)
    setUrl('')
    setTitle('')
    await refresh()
  }

  const showcaseTitle = (id: string | null) => showcases.find((s) => s.id === id)?.title

  async function toggleChunks(id: string) {
    if (openChunksFor === id) {
      setOpenChunksFor(null)
      return
    }
    const { chunks } = await listDocumentChunks(id)
    setChunks(chunks)
    setOpenChunksFor(id)
  }

  return (
    <div className="max-w-3xl space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="page-title">Dokumente</h2>
          <p className="text-sm text-text-muted">
            Unterlagen zu einzelnen Showcases. Alles über J4F, j4p und das Lab selbst gehört unter
            „Wissen".
          </p>
        </div>
        <ReindexAllButton onDone={() => void refresh()} />
      </div>

      <div className="card space-y-4 p-5">
        <h3 className="font-medium">Neues Dokument</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <select
            className="input"
            value={showcaseId}
            onChange={(e) => setShowcaseId(e.target.value)}
            aria-label="Showcase"
          >
            <option value="">Showcase wählen …</option>
            {showcases.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
          <input
            className="input"
            placeholder="Titel (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <FileButton
            label="Datei hochladen"
            disabled={!showcaseId}
            accept=".pdf,.docx,.pptx,.md,.markdown,.txt"
            hint={showcaseId ? 'PDF, Word, PowerPoint, Markdown, Text' : 'Erst Showcase wählen'}
            onFile={handleFileUpload}
          />
          <span className="text-center text-sm text-text-muted sm:pt-2.5">oder</span>
          <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row">
            <input
              className="input"
              type="url"
              inputMode="url"
              placeholder="https://…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <button
              onClick={() => void handleUrlUpload()}
              disabled={!showcaseId || !url.trim()}
              className="btn-primary shrink-0"
            >
              Link hinzufügen
            </button>
          </div>
        </div>
      </div>

      {documents.length === 0 ? (
        <p className="card p-5 text-sm text-text-muted">Noch keine Dokumente zu Showcases.</p>
      ) : (
        <ul className="card divide-y divide-border overflow-hidden">
          {documents.map((doc) => (
            <li key={doc.id}>
              <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{doc.title}</p>
                  <p className="mt-0.5 text-xs text-text-muted">
                    {STATUS_LABEL[doc.status]} · Version {doc.version}
                    {showcaseTitle(doc.showcaseId) && <> · {showcaseTitle(doc.showcaseId)}</>}
                  </p>
                  {doc.status === 'error' && doc.errorMessage && (
                    <p className="mt-1 text-xs text-status-error">{doc.errorMessage}</p>
                  )}
                  <WebseitenStand doc={doc} onDone={refresh} />
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <button onClick={() => void toggleChunks(doc.id)} className="btn-chip">
                    {openChunksFor === doc.id ? 'Abschnitte zu' : 'Abschnitte'}
                  </button>
                  <button
                    onClick={() => void reindexDocument(doc.id).then(refresh)}
                    className="btn-chip"
                  >
                    Neu indexieren
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`„${doc.title}" löschen?`)) {
                        void deleteDocument(doc.id).then(refresh)
                      }
                    }}
                    className="btn-chip-danger"
                  >
                    Löschen
                  </button>
                </div>
              </div>
              {openChunksFor === doc.id && (
                <ol className="list-decimal space-y-2 bg-bg-elevated px-5 py-3 pl-10 text-xs text-text-muted">
                  {chunks.length === 0 && (
                    <li className="-ml-5 list-none">Noch keine Abschnitte.</li>
                  )}
                  {chunks.map((c) => (
                    <li key={c.id} className="whitespace-pre-wrap">
                      {c.text}
                    </li>
                  ))}
                </ol>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Alle Dokumente neu indexieren — Showcase-Dokumente und Wissen zusammen, denn
 * der Grund ist meist derselbe (neue Zerlegung, leerer Index). Fragt einmal
 * nach: Jedes Dokument bekommt eine neue Version und läuft durch die
 * Warteschlange; bei vielen Dokumenten dauert das ein paar Minuten.
 */
export function ReindexAllButton({ onDone }: { onDone: () => void }) {
  const [busy, setBusy] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  async function run() {
    if (
      !window.confirm(
        'Alle Dokumente neu indexieren? Das dauert bei vielen Dokumenten ein paar Minuten.',
      )
    )
      return
    setBusy(true)
    setMeldung(null)
    try {
      const { count } = await reindexAllDocuments()
      setMeldung(`${count} Dokumente in der Warteschlange.`)
      onDone()
    } catch (err) {
      setMeldung(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="flex w-full flex-col gap-1 sm:w-auto sm:items-end">
      <button type="button" onClick={() => void run()} disabled={busy} className="btn-secondary">
        {busy ? 'Startet …' : 'Alle neu indexieren'}
      </button>
      {meldung && <span className="text-xs text-text-muted">{meldung}</span>}
    </div>
  )
}
